import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Share2 } from 'lucide-react';
import { SOCIAL_MEDIA_TIERS, SM_CURRENCY_SYMBOL } from '@/lib/socialMediaPricing';
import { detectDefaultCurrency, setStoredCurrency } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'lawagency360@gmail.com';

function formatAmount(n) { return Number(n).toLocaleString('en-US'); }

/**
 * Paywall modal for the Social Media add-on. Auto-opens when a client lands on
 * the Social Media tab without an active social_media plan. Underlying tab
 * content stays locked until the modal is dismissed (or a plan is purchased).
 */
export default function SocialMediaPaywallModal({ open, onClose }) {
  const [links, setLinks] = useState(null);
  const [currency, setCurrencyState] = useState(() => detectDefaultCurrency());
  const setCurrency = (c) => { setCurrencyState(c); setStoredCurrency(c); };
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    fetch(`${API}/stripe/social-media-links`).then(r => r.json()).then(setLinks).catch(() => setLinks({}));
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleChoose = (tierId) => {
    setLoading(tierId);
    try {
      // Mark "paying now" so the parent tab polls billing on return
      try { localStorage.setItem('social_media_paying_at', new Date().toISOString()); } catch (_e) { /* ignore */ }
      const url = links?.[tierId]?.[currency];
      const isPlaceholder = !url || url.toLowerCase().includes('paste payment link');
      if (!isPlaceholder) {
        window.location.href = url;
        return;
      }
      const tier = SOCIAL_MEDIA_TIERS.find(t => t.id === tierId);
      const subject = encodeURIComponent(`Activate Social Media - ${tier?.label || tierId}`);
      const body = encodeURIComponent(`Hi Law Agency 360 team,\n\nPlease activate the ${tier?.label || tierId} so I can start the social media workflow.\n\nThanks!`);
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    } finally {
      setLoading(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="social-media-paywall-title"
      data-testid="social-media-paywall-modal"
    >
      <button
        type="button"
        aria-label="Close paywall"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-3xl rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-violet-500/30 p-7 shadow-2xl shadow-violet-900/40 my-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="social-media-paywall-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
            <Share2 className="w-5 h-5 text-violet-300" />
          </div>
          <div>
            <h2 id="social-media-paywall-title" className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
              Unlock Social Media Management
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5">
              Choose a monthly plan to activate the social media workflow.
            </p>
          </div>
        </div>

        {/* Currency toggle */}
        <div className="flex justify-end mt-5 mb-4">
          <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full" data-testid="social-media-paywall-currency">
            {[
              { code: 'gbp', label: 'GBP £' },
              { code: 'eur', label: 'EUR €' },
              { code: 'usd', label: 'USD $' },
            ].map(c => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                  currency === c.code ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`social-media-paywall-currency-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4" data-testid="social-media-paywall-tiers">
          {SOCIAL_MEDIA_TIERS.map(t => {
            const sym = SM_CURRENCY_SYMBOL[currency] || '£';
            const amt = t.price[currency];
            return (
              <div
                key={t.id}
                className={`relative flex flex-col rounded-2xl p-6 border transition-all ${
                  t.featured
                    ? 'bg-gradient-to-b from-violet-900/30 to-indigo-900/20 border-violet-500/40 shadow-xl shadow-violet-900/30'
                    : 'bg-slate-800/40 border-white/10 hover:border-white/20'
                }`}
                data-testid={`social-media-paywall-tier-${t.id}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{t.label}</h3>
                <p className="text-sm text-zinc-400 mb-5">{t.positioning}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-bold text-white" data-testid={`social-media-paywall-price-${t.id}`}>
                    {sym}{formatAmount(amt)}
                  </span>
                  <span className="text-sm text-zinc-400">/month</span>
                </div>
                <div className="flex items-center gap-2 mb-4 text-sm text-zinc-200">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>Monthly billing · Cancel anytime</span>
                </div>
                <ul className="space-y-2 mb-6 flex-1" data-testid={`social-media-paywall-features-${t.id}`}>
                  {(t.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleChoose(t.id)}
                  disabled={loading === t.id}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    t.featured
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-violet-600/40'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  data-testid={`social-media-paywall-choose-${t.id}`}
                >
                  {loading === t.id ? 'Redirecting…' : 'Unlock Now'}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-zinc-500 mt-5 text-center">
          Your dashboard activates automatically the moment Stripe confirms payment.
        </p>
      </div>
    </div>,
    document.body
  );
}
